import React from 'react';
import { cn } from '@/lib/utils';

interface RichTextDisplayProps {
  content: string;
  className?: string;
  maxLines?: number;
}

/**
 * Component for displaying rich text content (HTML from React-Quill)
 * Safely renders HTML with sanitization
 */
export const RichTextDisplay: React.FC<RichTextDisplayProps> = ({
  content,
  className,
  maxLines,
}) => {
  if (!content) return null;

  // Basic HTML sanitization - only allow safe tags
  const sanitizeHTML = (html: string): string => {
    if (!html) return '';
    
    // Remove script tags and event handlers
    let sanitized = html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/on\w+="[^"]*"/gi, '')
      .replace(/on\w+='[^']*'/gi, '');
    
    // Only allow safe HTML tags
    const allowedTags = ['b', 'strong', 'i', 'em', 'u', 'ul', 'ol', 'li', 'p', 'br'];
    
    // Remove any tags not in allowed list
    const tagPattern = new RegExp(`<(?!/?(${allowedTags.join('|')})\\b)[^>]+>`, 'gi');
    sanitized = sanitized.replace(tagPattern, '');
    
    // Remove attributes from allowed tags
    allowedTags.forEach(tag => {
      sanitized = sanitized.replace(new RegExp(`<${tag}\\s+[^>]*>`, 'gi'), `<${tag}>`);
    });
    
    return sanitized;
  };

  const sanitizedContent = sanitizeHTML(content);

  // Plaintext fallback if content is empty or only whitespace
  if (!sanitizedContent.trim() || sanitizedContent.trim() === '<p><br></p>') {
    return null;
  }

  return (
    <div
      className={cn(
        'rich-text-display text-xs text-muted-foreground',
        maxLines === 1 && 'line-clamp-1',
        maxLines === 2 && 'line-clamp-2',
        maxLines === 3 && 'line-clamp-3',
        className
      )}
      style={{
        wordBreak: 'break-word',
        whiteSpace: 'normal',
        ...(maxLines && maxLines > 3 && {
          display: '-webkit-box',
          WebkitLineClamp: maxLines,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }),
      }}
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
    />
  );
};
